import React, { useEffect, useState } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import api from '../../utils/api';

interface BookshelfBook {
  id: number;
  bookId: number;
  addedAt: string;
  book: {
    id: number;
    title: string;
    author?: string;
    cover?: string;
    format: string;
  };
}

interface Bookshelf {
  id: number;
  name: string;
  description?: string;
  ownerId: number;
  bookCount: number;
  books: BookshelfBook[];
}

const BookshelfDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  
  const [bookshelf, setBookshelf] = useState<Bookshelf | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);

  const isOwner = isAuthenticated && user?.id === bookshelf?.ownerId;

  useEffect(() => {
    fetchBookshelf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBookshelf = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookshelves/${id}`);
      setBookshelf(data);
      setEditName(data.name);
      setEditDescription(data.description || '');
    } catch (error) {
      console.error('获取书单详情失败:', error);
      history.push('/');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editName.trim()) return;
    try {
      await api.put(`/bookshelves/${id}`, {
        name: editName.trim(),
        description: editDescription
      });
      setIsEditing(false);
      fetchBookshelf();
    } catch (error) {
      alert('更新失败');
    }
  };

  const handleDeleteBookshelf = async () => {
    try {
      await api.delete(`/bookshelves/${id}`);
      history.push(`/profile/${user?.id}`);
    } catch (error) {
      alert('删除失败');
    }
  };

  const handleRemoveBook = async (bookId: number) => {
    try {
      await api.delete(`/bookshelves/books/${id}/${bookId}`);
      fetchBookshelf();
    } catch (error) {
      alert('移除失败');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!bookshelf) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <p>书单不存在</p>
      </div>
    );
  }

  return (
    <div>
      {/* 书单头部 */}
      <div className="card" style={{ marginBottom: '24px' }}>
        {isEditing ? (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px' }}>书单名称</label>
              <input
                type="text"
                className="form-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{ maxWidth: '400px' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px' }}>描述</label>
              <textarea
                className="form-input"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                style={{ maxWidth: '400px', minHeight: '80px' }}
                placeholder="添加描述..."
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleUpdate} className="btn btn-primary">
                保存
              </button>
              <button onClick={() => setIsEditing(false)} className="btn btn-default">
                取消
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ marginBottom: '8px' }}>{bookshelf.name}</h2>
                {bookshelf.description && (
                  <p style={{ color: '#666', marginBottom: '12px' }}>{bookshelf.description}</p>
                )}
                <p style={{ color: '#999', fontSize: '14px' }}>
                  共 {bookshelf.bookCount} 本图书
                </p>
              </div>
              {isOwner && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={() => setIsEditing(true)} 
                    className="btn btn-default"
                    style={{ padding: '6px 16px' }}
                  >
                    编辑
                  </button>
                  <button 
                    onClick={handleDeleteBookshelf} 
                    className="btn"
                    style={{ 
                      padding: '6px 16px',
                      background: '#ff4d4f',
                      color: '#fff',
                      border: 'none'
                    }}
                  >
                    删除书单
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 图书列表 */}
      <div className="card">
        <h3 style={{ marginBottom: '16px' }}>图书列表</h3>
        {bookshelf.books && bookshelf.books.length > 0 ? (
          <div className="book-grid">
            {bookshelf.books.map((item) => (
              <div key={item.id} className="book-card" style={{ position: 'relative' }}>
                <Link to={`/book/${item.book.id}`}>
                  {item.book.cover ? (
                    <img src={item.book.cover} alt={item.book.title} className="book-cover" />
                  ) : (
                    <div 
                      className="book-cover"
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '14px',
                        background: '#f5f5f5'
                      }}
                    >
                      暂无封面
                    </div>
                  )}
                </Link>
                <div className="book-info">
                  <Link to={`/book/${item.book.id}`} style={{ textDecoration: 'none' }}>
                    <h3 className="book-title" style={{ fontSize: '14px' }}>{item.book.title}</h3>
                  </Link>
                  <p className="book-author">{item.book.author || '未知作者'}</p>
                  <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    {item.book.format.toUpperCase()}
                  </p>
                  <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    添加于: {new Date(item.addedAt).toLocaleDateString()}
                  </p>
                </div>
                {isOwner && (
                  <button
                    onClick={() => setShowDeleteConfirm(item.bookId)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(0,0,0,0.5)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    移除
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#999', textAlign: 'center', padding: '40px' }}>
            书单中暂无图书
          </p>
        )}
      </div>

      {/* 移除确认弹窗 */}
      {showDeleteConfirm !== null && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '300px', textAlign: 'center' }}>
            <h3 style={{ marginBottom: '16px' }}>确认移除</h3>
            <p style={{ marginBottom: '24px', color: '#666' }}>
              确定要从书单中移除这本书吗？
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button 
                onClick={() => handleRemoveBook(showDeleteConfirm)}
                className="btn"
                style={{ background: '#ff4d4f', color: '#fff', border: 'none' }}
              >
                确定移除
              </button>
              <button 
                onClick={() => setShowDeleteConfirm(null)}
                className="btn btn-default"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookshelfDetail;
